import { IonGrid, IonItem, IonInput, IonLabel, IonRow, IonTextarea, useIonToast } from '@ionic/react';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import InputError from 'components/InputError';
import clsx from 'clsx';
import { TOAST_DURATION } from '../../constants';

const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        // Validate that the file is a valid Blob
        if (!file || !(file instanceof Blob)) {
            reject(new Error('Invalid file object'));
            return;
        }
        
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
};

type FormValues = {
    prompt: string;
    image?: FileList;
};

interface LetzAiPromptProps {
    onComplete: (prompt: string, imageBase64?: string) => void;
    description: string;
    promptPlaceholder: string;
    allowUserToUploadImages: boolean;
}

export default function LetzAiPrompt({
    onComplete,
    description,
    promptPlaceholder,
    allowUserToUploadImages,
}: LetzAiPromptProps) {
    const { t } = useTranslation();
    const [present] = useIonToast();

    const schema = yup
        .object({
            prompt: yup.string().required(t('form.prompt_required')),
            image: allowUserToUploadImages ? yup.mixed().optional() : yup.mixed().optional(),
        })
        .required();

    const {
        register,
        handleSubmit,
        formState: { isValid, errors },
        control,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data: FormValues) => {
        try {
            let imageBase64: string | undefined;
            
            if (data.image && data.image.length > 0) {
                try {
                    const file = data.image[0]; // Get the first file from FileList
                    imageBase64 = await convertFileToBase64(file);
                } catch (fileError: any) {
                    await present({ 
                        message: t('letzAi.imageUploadError') || 'Error uploading image. Please try again.', 
                        duration: TOAST_DURATION, 
                        position: 'top', 
                        color: 'danger' 
                    });
                    return;
                }
            }
            
            present({ color: 'success', duration: TOAST_DURATION, message: t('letzAi.promptSend') });
            onComplete(data.prompt, imageBase64);
        } catch (e: any) {
            const message: string = e.error || e.response?.data?.error || e.response?.message || e.message || 'Error';
            await present({ message, duration: TOAST_DURATION, position: 'top', color: 'danger' });
        }
    };

    return (
        <IonGrid
            fixed
            className="md:flex md:justify-center md:bg-[#141414]"
        >
            <div className="md:w-[550px] w-full">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-[30px]"
                >
                    <div>{description}</div>
                    {allowUserToUploadImages && (
                        <IonRow>
                            <IonItem className="bg-[#343434] w-full rounded-t">
                                <IonLabel
                                    className="game-input pt-1"
                                    position="stacked"
                                >
                                    {t('letzAi.uploadImages')}
                                </IonLabel>
                                <input
                                    required={allowUserToUploadImages}
                                    {...register('image')}
                                    type="file"
                                    accept="image/*"
                                    multiple={false}
                                    className="py-4 max-md:mt-8 mt-4 w-full text-white h-[70px]"
                                    style={{ background: 'transparent' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            // Check file type
                                            if (!file.type.startsWith('image/')) {
                                                present({ 
                                                    message: t('letzAi.invalidImageType') || 'Please select a valid image file.', 
                                                    duration: TOAST_DURATION, 
                                                    position: 'top', 
                                                    color: 'danger' 
                                                });
                                                e.target.value = '';
                                                return;
                                            }
                                            
                                            // Check file size (max 5MB)
                                            const maxSize = 5 * 1024 * 1024; // 5MB
                                            if (file.size > maxSize) {
                                                present({ 
                                                    message: t('letzAi.fileTooLarge') || 'File size must be less than 10MB.', 
                                                    duration: TOAST_DURATION, 
                                                    position: 'top', 
                                                    color: 'danger' 
                                                });
                                                e.target.value = '';
                                                return;
                                            }
                                        }
                                    }}
                                />
                            </IonItem>
                        </IonRow>
                    )}
                    <IonRow>
                        <IonItem className="bg-[#343434] w-full rounded-t">
                            <IonLabel
                                className="game-input pt-1"
                                position="stacked"
                            >
                                {promptPlaceholder || t('letzAi.prompt')}
                            </IonLabel>
                            <IonTextarea
                                {...register('prompt')}
                                style={{ minHeight: 220 }}
                                className="py-4 max-md:mt-8"
                            />
                        </IonItem>
                    </IonRow>

                    <IonRow>
                        <div>
                            <ErrorMessage
                                errors={errors}
                                name="prompt"
                                as={<InputError />}
                            />
                        </div>
                        <Button
                            value={t('letzAi.send')}
                            className={clsx(
                                'border-0 !text-[#141414] bg-white hover:bg-[#e5e0e0] transition duration-300',
                            )}
                            type="submit"
                            disabled={false}
                        />
                    </IonRow>
                </form>
            </div>
        </IonGrid>
    );
}
